"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapV1DraftToRevisionV2 = mapV1DraftToRevisionV2;
const revision_constants_js_1 = require("./revision.constants.js");
const revision_gating_js_1 = require("./revision-gating.js");
function datesEqual(a, b) {
    return (0, revision_gating_js_1.toDateOnly)(a) === (0, revision_gating_js_1.toDateOnly)(b);
}
function textEqual(a, b) {
    return (a || "").trim() === (b || "").trim();
}
function isLegacyVo(row) {
    return row?.metadata?.["isVariationOrder"] === true;
}
function emptyV2() {
    return {
        schemaVersion: revision_constants_js_1.REVISION_SCHEMA_V2,
        milestoneChanges: [],
        milestonesAdded: [],
        milestonesDeleted: [],
        submilestoneChanges: [],
        submilestonesAdded: [],
        submilestonesDeleted: [],
        invoiceChanges: [],
        adjustmentIds: [],
        rounds: [],
    };
}
/**
 * Convert an unsent v1 snapshot draft into sparse v2 metadata.
 * Milestone/sub budget deltas and legacy VO-milestones are recorded in `skippedMoneyEdits`
 * and are not turned into adjustments (avoids deduction auto-assign).
 */
function mapV1DraftToRevisionV2(live, raw) {
    const v1 = (raw ?? {});
    const metadata = emptyV2();
    const skippedMoneyEdits = [];
    if (v1.noteToClient)
        metadata.noteToClient = String(v1.noteToClient);
    if (v1.projectStartDate !== undefined && !datesEqual(v1.projectStartDate, live.startDate)) {
        metadata.projectStartDate = (0, revision_gating_js_1.toDateOnly)(v1.projectStartDate);
    }
    if (v1.projectEndDate !== undefined && !datesEqual(v1.projectEndDate, live.endDate)) {
        metadata.projectEndDate = (0, revision_gating_js_1.toDateOnly)(v1.projectEndDate);
    }
    const proposedMilestones = (Array.isArray(v1.milestonesRevision) ? v1.milestonesRevision : []);
    const proposedById = new Map(proposedMilestones.filter((row) => row.id).map((row) => [String(row.id), row]));
    const deletedList = (Array.isArray(v1.deletedMilestones) ? v1.deletedMilestones : []);
    const deletedIds = new Set(deletedList.map((row) => String(row.id || "")).filter(Boolean));
    for (const liveMilestone of live.milestones) {
        if (deletedIds.has(liveMilestone.id) || !proposedById.has(liveMilestone.id)) {
            const deleted = deletedList.find((row) => row.id === liveMilestone.id);
            metadata.milestonesDeleted.push({
                id: liveMilestone.id,
                reason: (deleted?.reason || "").trim() || "Removed in previous draft",
            });
            continue;
        }
        const proposed = proposedById.get(liveMilestone.id);
        if (isLegacyVo(proposed)) {
            skippedMoneyEdits.push({
                kind: "legacy_variation_order",
                id: liveMilestone.id,
                label: proposed.name || liveMilestone.name,
                detail: "Legacy variation-order milestones are not imported into the new editor",
            });
        }
        const fields = {};
        if (proposed.name != null && !textEqual(proposed.name, liveMilestone.name))
            fields.name = String(proposed.name);
        if (proposed.description !== undefined && !textEqual(proposed.description ?? null, liveMilestone.description ?? null)) {
            fields.description = proposed.description ?? null;
        }
        if (proposed.startDate !== undefined && !datesEqual(proposed.startDate, liveMilestone.startDate)) {
            fields.startDate = (0, revision_gating_js_1.toDateOnly)(proposed.startDate);
        }
        if (proposed.endDate !== undefined && !datesEqual(proposed.endDate, liveMilestone.endDate)) {
            fields.endDate = (0, revision_gating_js_1.toDateOnly)(proposed.endDate);
        }
        if (Object.keys(fields).length > 0) {
            metadata.milestoneChanges.push({
                id: liveMilestone.id,
                fields,
                ...(proposed.reason ? { reason: String(proposed.reason) } : {}),
            });
        }
        const proposedBudget = Number(proposed.budgetAmount ?? liveMilestone.budgetAmount);
        if (Number.isFinite(proposedBudget) && Math.abs(proposedBudget - Number(liveMilestone.budgetAmount || 0)) > 0.009) {
            skippedMoneyEdits.push({
                kind: "milestone_budget",
                id: liveMilestone.id,
                label: proposed.name || liveMilestone.name,
                detail: `Budget change ${liveMilestone.budgetAmount} → ${proposedBudget} was not imported`,
            });
        }
        const liveSubs = liveMilestone.subMilestones ?? [];
        const proposedSubs = proposed.subMilestones ?? [];
        const proposedSubById = new Map(proposedSubs.filter((sub) => sub.id).map((sub) => [String(sub.id), sub]));
        for (const liveSub of liveSubs) {
            const proposedSub = proposedSubById.get(liveSub.id);
            if (!proposedSub) {
                metadata.submilestonesDeleted.push({ id: liveSub.id, parentMilestoneId: liveMilestone.id });
                continue;
            }
            const subFields = {};
            if (proposedSub.name != null && !textEqual(proposedSub.name, liveSub.name))
                subFields.name = String(proposedSub.name);
            if (proposedSub.description !== undefined && !textEqual(proposedSub.description ?? null, liveSub.description ?? null)) {
                subFields.description = proposedSub.description ?? null;
            }
            if (proposedSub.startDate !== undefined && !datesEqual(proposedSub.startDate, liveSub.startDate)) {
                subFields.startDate = (0, revision_gating_js_1.toDateOnly)(proposedSub.startDate);
            }
            if (proposedSub.endDate !== undefined && !datesEqual(proposedSub.endDate, liveSub.endDate)) {
                subFields.endDate = (0, revision_gating_js_1.toDateOnly)(proposedSub.endDate);
            }
            const proposedSubAmount = Number(proposedSub.budgetAmount ?? liveSub.budgetAmount);
            if (Number.isFinite(proposedSubAmount) && Math.abs(proposedSubAmount - Number(liveSub.budgetAmount || 0)) > 0.009) {
                skippedMoneyEdits.push({
                    kind: "submilestone_amount",
                    id: liveSub.id,
                    label: proposedSub.name || liveSub.name,
                    detail: `Sub-milestone amount change was not imported`,
                });
            }
            if (Object.keys(subFields).length > 0) {
                metadata.submilestoneChanges.push({
                    id: liveSub.id,
                    parentMilestoneId: liveMilestone.id,
                    fields: subFields,
                });
            }
        }
        for (const proposedSub of proposedSubs) {
            if (proposedSub.id && liveSubs.some((sub) => sub.id === proposedSub.id))
                continue;
            if (isLegacyVo(proposedSub)) {
                skippedMoneyEdits.push({
                    kind: "legacy_variation_order",
                    id: String(proposedSub.id || proposedSub.name || "new-vo"),
                    label: proposedSub.name || "Variation order",
                    detail: "Legacy variation-order sub-milestones are not imported into the new editor",
                });
                continue;
            }
            const tempId = proposedSub.id ? String(proposedSub.id) : `tmp-sub-${metadata.submilestonesAdded.length + 1}`;
            metadata.submilestonesAdded.push({
                tempId,
                parentMilestoneId: liveMilestone.id,
                name: String(proposedSub.name || "Sub-milestone"),
                description: proposedSub.description ?? undefined,
                amount: Number(proposedSub.budgetAmount || 0),
                startDate: (0, revision_gating_js_1.toDateOnly)(proposedSub.startDate),
                endDate: (0, revision_gating_js_1.toDateOnly)(proposedSub.endDate),
            });
        }
    }
    for (const proposed of proposedMilestones) {
        const id = proposed.id ? String(proposed.id) : "";
        if (id && live.milestones.some((milestone) => milestone.id === id))
            continue;
        if (isLegacyVo(proposed)) {
            skippedMoneyEdits.push({
                kind: "legacy_variation_order",
                id: id || String(proposed.name || "new-vo"),
                label: proposed.name || "Variation order",
                detail: "Legacy variation-order milestones are not imported into the new editor",
            });
            continue;
        }
        const tempId = id || `tmp-ms-${metadata.milestonesAdded.length + 1}`;
        metadata.milestonesAdded.push({
            tempId,
            name: String(proposed.name || "Milestone"),
            description: proposed.description ?? undefined,
            budgetAmount: Number(proposed.budgetAmount || 0),
            startDate: (0, revision_gating_js_1.toDateOnly)(proposed.startDate) || (0, revision_gating_js_1.toDateOnly)(live.startDate) || "",
            endDate: (0, revision_gating_js_1.toDateOnly)(proposed.endDate) || (0, revision_gating_js_1.toDateOnly)(live.endDate) || "",
            reason: (proposed.reason || "").trim() || "Added in previous draft",
        });
    }
    const proposedInvoices = (Array.isArray(v1.invoicesRevision) ? v1.invoicesRevision : []);
    const proposedInvoiceById = new Map(proposedInvoices.filter((row) => row.id).map((row) => [String(row.id), row]));
    for (const liveInvoice of live.invoices) {
        const proposed = proposedInvoiceById.get(liveInvoice.id);
        if (!proposed) {
            if (liveInvoice.status === "pending" || liveInvoice.status === "draft") {
                metadata.invoiceChanges.push({ id: liveInvoice.id, delete: true });
            }
            continue;
        }
        const milestoneIds = Array.isArray(proposed.milestoneIds) ? proposed.milestoneIds.filter((id) => typeof id === "string") : liveInvoice.milestoneIds;
        const dueChanged = proposed.dueDate !== undefined && (0, revision_gating_js_1.toDateOnly)(proposed.dueDate) !== (0, revision_gating_js_1.toDateOnly)(liveInvoice.dueDate);
        const milestoneChanged = milestoneIds.length !== liveInvoice.milestoneIds.length || milestoneIds.some((id, index) => id !== liveInvoice.milestoneIds[index]);
        if (milestoneChanged || dueChanged) {
            metadata.invoiceChanges.push({
                id: liveInvoice.id,
                ...(dueChanged ? { dueDate: (0, revision_gating_js_1.toDateOnly)(proposed.dueDate) } : {}),
                ...(milestoneChanged ? { milestoneIds } : {}),
            });
        }
    }
    for (const proposed of proposedInvoices) {
        if (proposed.id && live.invoices.some((invoice) => invoice.id === proposed.id))
            continue;
        if (proposed.delete)
            continue;
        const milestoneIds = Array.isArray(proposed.milestoneIds) ? proposed.milestoneIds.filter((id) => typeof id === "string") : [];
        metadata.invoiceChanges.push({
            tempId: String(proposed.tempId || proposed.id || `tmp-inv-${metadata.invoiceChanges.length + 1}`),
            isNew: true,
            dueDate: (0, revision_gating_js_1.toDateOnly)(proposed.dueDate),
            milestoneIds,
            adjustmentIds: [],
        });
    }
    return { metadata, skippedMoneyEdits };
}
